import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { saTodayISO } from '../../lib/dateUtils';
import { API_URL, auth, errMsg, CATEGORIES } from './financeApi';

export const ExpenseDialog = ({ open, onOpenChange, vehicles, expense, token, onDone }) => {
  const editing = !!expense;
  const [vehicleId, setVehicleId] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('petrol');
  const [date, setDate] = useState(saTodayISO());
  const [slip, setSlip] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVehicleId(expense?.vehicle_id || (vehicles.length === 1 ? vehicles[0].vehicle_id : ''));
    setAmount(expense ? String(expense.amount) : '');
    setCategory(expense?.category || 'petrol');
    setDate(expense?.date || saTodayISO());
    setSlip(null);
    setPreview(null);
  }, [open, expense, vehicles]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSlip(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    const form = new FormData();
    form.append('amount', amount);
    form.append('category', category);
    form.append('expense_date', date);
    if (slip) form.append('slip', slip);
    if (!editing) form.append('vehicle_id', vehicleId);
    setBusy(true);
    try {
      const res = editing
        ? await axios.put(`${API_URL}/finance/expenses/${expense.expense_id}`, form, auth(token))
        : await axios.post(`${API_URL}/finance/expenses`, form, auth(token));
      toast.success(res.data.message);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save expense'));
    } finally {
      setBusy(false);
    }
  };

  const ready = amount && category && date && (editing || (vehicleId && slip));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit expense' : 'Add an expense'}</DialogTitle>
          <DialogDescription>{editing ? 'Changes are logged with the old and new value.' : 'Snap the slip, type the amount and save. You can edit it for 24 hours.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!editing && vehicles.length > 1 && (
            <div>
              <Label>Vehicle</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger data-testid="expense-vehicle-select"><SelectValue placeholder="Choose vehicle" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.registration_number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Slip photo{editing ? ' (optional — replace)' : ''}</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer hover:bg-muted/40 text-sm" data-testid="expense-camera-label">
                <Camera className="w-4 h-4" /> Take photo
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="expense-camera-input" />
              </label>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer hover:bg-muted/40 text-sm" data-testid="expense-upload-label">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="expense-slip-input" />
              </label>
            </div>
            {preview && <img src={preview} alt="Slip preview" className="mt-2 max-h-40 rounded-lg border object-contain w-full" data-testid="expense-slip-preview" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-amount">Amount (R)</Label>
              <Input id="exp-amount" type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="expense-amount-input" />
            </div>
            <div>
              <Label htmlFor="exp-date">Date</Label>
              <Input id="exp-date" type="date" max={saTodayISO()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="expense-date-input" />
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {CATEGORIES.map(({ value, label, icon: Icon }) => (
                <button key={value} type="button" onClick={() => setCategory(value)}
                  className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-xs transition-colors ${category === value ? 'border-emerald-600 bg-emerald-50 text-emerald-800 font-semibold' : 'hover:bg-muted/40'}`}
                  data-testid={`expense-type-${value}`}>
                  <Icon className="w-5 h-5" /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="expense-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy || !ready} data-testid="expense-save-btn">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
